import type { StaffBooking, StaffBookingTab } from '../types/staffBookings';

/**
 * Pure helpers behind the staff Bookings calendar.
 *
 * Dates are handled as `YYYY-MM-DD` keys and minutes-from-midnight, never as Date objects
 * carrying a timezone: appointmentDateTime is the salon's wall-clock time (BUSINESS_ZONE on the
 * server), so "today", the current-time line and the travel checks are all judged on that clock.
 */

/** Mirrors ServiceConstants.BUSINESS_ZONE on the backend. */
export const BUSINESS_TIME_ZONE = 'America/Chicago';

export type CalendarMode = 'day' | 'week' | 'month';
export type ColorMode = 'salon' | 'status';
export type CalendarStatus = 'upcoming' | 'previous' | 'completed' | 'cancelled';

export interface DateRange {
	from: string;
	to: string;
}

export interface EventColor {
	border: string;
	background: string;
	text: string;
}

/** The 45-minute rule between salons (StaffScheduleConflictChecker on the backend). */
export const MIN_TRAVEL_MINUTES = 45;

export const MINUTES_IN_DAY = 24 * 60;

// ─── Date keys ────────────────────────────────────────────────────────────

const pad = (n: number): string => String(n).padStart(2, '0');

export const dateKey = (d: Date): string =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const parseKey = (key: string): Date => {
	const [y, m, d] = key.split('-').map(Number);
	return new Date(y, m - 1, d);
};

export const addDays = (key: string, days: number): string => {
	const d = parseKey(key);
	return dateKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
};

export const addMonths = (key: string, months: number): string => {
	const d = parseKey(key);
	return dateKey(new Date(d.getFullYear(), d.getMonth() + months, 1));
};

/** Monday on or before the key. */
export const startOfWeek = (key: string): string => {
	const d = parseKey(key);
	return addDays(key, -((d.getDay() + 6) % 7));
};

export const monthStart = (key: string): string => `${key.slice(0, 7)}-01`;

export const monthEnd = (key: string): string => {
	const d = parseKey(key);
	return dateKey(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

export const sameMonth = (a: string, b: string): boolean => a.slice(0, 7) === b.slice(0, 7);

export const daysBetween = (from: string, to: string): number =>
	Math.round((parseKey(to).getTime() - parseKey(from).getTime()) / 86_400_000);

export const daysInRange = (from: string, to: string): string[] => {
	const out: string[] = [];
	const n = daysBetween(from, to);
	for (let i = 0; i <= n; i++) out.push(addDays(from, i));
	return out;
};

/** Today's date on the salon clock. */
export const todayKey = (now: Date = new Date()): string =>
	new Intl.DateTimeFormat('en-CA', {
		timeZone: BUSINESS_TIME_ZONE,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).format(now);

/** Minutes since midnight on the salon clock. */
export const nowMinutes = (now: Date = new Date()): number => {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: BUSINESS_TIME_ZONE,
		hour: 'numeric',
		minute: 'numeric',
		hourCycle: 'h23',
	}).formatToParts(now);
	const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
	return get('hour') * 60 + get('minute');
};

// ─── Visible period ───────────────────────────────────────────────────────

export const rangeFor = (mode: CalendarMode, anchor: string): DateRange => {
	if (mode === 'day') return { from: anchor, to: anchor };
	if (mode === 'week') {
		const from = startOfWeek(anchor);
		return { from, to: addDays(from, 6) };
	}
	return { from: monthStart(anchor), to: monthEnd(anchor) };
};

export const shiftAnchor = (mode: CalendarMode, anchor: string, steps: number): string => {
	if (mode === 'day') return addDays(anchor, steps);
	if (mode === 'week') return addDays(anchor, 7 * steps);
	return addMonths(anchor, steps);
};

/** The six Monday-to-Sunday weeks a month grid shows, adjacent-month days included. */
export const monthGridRange = (monthKey: string): DateRange => {
	const from = startOfWeek(monthStart(monthKey));
	return { from, to: addDays(from, 41) };
};

/** The weeks the month view renders: every week that touches the month. */
export const monthViewRange = (monthKey: string): DateRange => ({
	from: startOfWeek(monthStart(monthKey)),
	to: addDays(startOfWeek(monthEnd(monthKey)), 6),
});

export const unionRange = (a: DateRange, b: DateRange): DateRange => ({
	from: a.from < b.from ? a.from : b.from,
	to: a.to > b.to ? a.to : b.to,
});

export const rangeContains = (outer: DateRange, inner: DateRange): boolean =>
	outer.from <= inner.from && inner.to <= outer.to;

// ─── Bookings on the clock ────────────────────────────────────────────────

export const bookingDateKey = (b: Pick<StaffBooking, 'appointmentDateTime'>): string =>
	b.appointmentDateTime.slice(0, 10);

export const startMinutes = (b: Pick<StaffBooking, 'appointmentDateTime'>): number => {
	const h = Number(b.appointmentDateTime.slice(11, 13));
	const m = Number(b.appointmentDateTime.slice(14, 16));
	return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
};

export const endMinutes = (
	b: Pick<StaffBooking, 'appointmentDateTime' | 'durationMinutes'>
): number => startMinutes(b) + (b.durationMinutes || 0);

/** Done from this staff member's side: the salon closed it or they filed their TCR. */
export const isCompletedForStaff = (b: Pick<StaffBooking, 'status' | 'tcrStatus'>): boolean =>
	b.status === 'COMPLETED' || !!b.tcrStatus;

export const calendarStatus = (
	b: Pick<StaffBooking, 'status' | 'tcrStatus' | 'appointmentDateTime'>,
	today: string
): CalendarStatus => {
	if (b.status === 'CANCELLED') return 'cancelled';
	if (isCompletedForStaff(b)) return 'completed';
	return bookingDateKey(b) < today ? 'previous' : 'upcoming';
};

/** The list tab a booking sits in, for "Show in list". */
export const bookingTab = (
	b: Pick<StaffBooking, 'status' | 'tcrStatus' | 'appointmentDateTime'>,
	today: string
): StaffBookingTab => {
	const status = calendarStatus(b, today);
	if (status === 'cancelled' || status === 'completed') return status;
	const day = bookingDateKey(b);
	if (day === today) return 'today';
	return day < today ? 'previous' : 'upcoming';
};

export const byStart = (a: StaffBooking, b: StaffBooking): number =>
	a.appointmentDateTime.localeCompare(b.appointmentDateTime);

// ─── Lanes for overlapping events ─────────────────────────────────────────

export interface LanePlacement {
	lane: number;
	lanes: number;
}

/**
 * Side-by-side lanes for bookings that overlap on one day, so neither hides the other. Each
 * overlap cluster gets its own lane count; cancelled bookings take part like any other.
 */
export const laneLayout = (dayItems: StaffBooking[]): Map<number, LanePlacement> => {
	const sorted = [...dayItems].sort(byStart);
	const placements = new Map<number, LanePlacement>();
	let cluster: { end: number; laneEnd: number[]; ids: number[] } | null = null;
	const close = () => {
		if (!cluster) return;
		const lanes = cluster.laneEnd.length;
		for (const id of cluster.ids) {
			const p = placements.get(id);
			if (p) p.lanes = lanes;
		}
	};
	for (const b of sorted) {
		const s = startMinutes(b);
		const e = Math.max(s + 1, Math.min(MINUTES_IN_DAY, endMinutes(b)));
		if (!cluster || s >= cluster.end) {
			close();
			cluster = { end: e, laneEnd: [], ids: [] };
		}
		let lane = cluster.laneEnd.findIndex(laneEnd => s >= laneEnd);
		if (lane === -1) {
			lane = cluster.laneEnd.length;
			cluster.laneEnd.push(e);
		} else {
			cluster.laneEnd[lane] = e;
		}
		cluster.end = Math.max(cluster.end, e);
		cluster.ids.push(b.id);
		placements.set(b.id, { lane, lanes: 1 });
	}
	close();
	return placements;
};

// ─── Travel between salons ────────────────────────────────────────────────

export interface TravelGap {
	from: StaffBooking;
	to: StaffBooking;
	/** Minutes between the earlier booking's end and the next one's start; negative = overlap. */
	gapMinutes: number;
	tight: boolean;
}

/**
 * Consecutive non-cancelled bookings on one day at different locations. A gap under 45 minutes,
 * overlaps included, is flagged; same-location pairs never are.
 */
export const travelGaps = (dayItems: StaffBooking[]): TravelGap[] => {
	const live = dayItems.filter(b => b.status !== 'CANCELLED').sort(byStart);
	const out: TravelGap[] = [];
	for (let i = 1; i < live.length; i++) {
		const from = live[i - 1];
		const to = live[i];
		const fromLoc = from.location?.id;
		const toLoc = to.location?.id;
		if (fromLoc == null || toLoc == null || fromLoc === toLoc) continue;
		const gapMinutes = startMinutes(to) - endMinutes(from);
		out.push({ from, to, gapMinutes, tight: gapMinutes < MIN_TRAVEL_MINUTES });
	}
	return out;
};

export const groupByDay = (items: StaffBooking[]): Map<string, StaffBooking[]> => {
	const map = new Map<string, StaffBooking[]>();
	for (const b of items) {
		const key = bookingDateKey(b);
		const list = map.get(key);
		if (list) list.push(b);
		else map.set(key, [b]);
	}
	for (const list of map.values()) list.sort(byStart);
	return map;
};

// ─── Workload summary ─────────────────────────────────────────────────────

export interface WorkloadSummary {
	bookings: number;
	minutes: number;
	/** Sum of booking prices, tips excluded. Not staff earnings. */
	serviceValue: number;
	salons: number;
}

export const summarize = (items: StaffBooking[]): WorkloadSummary => {
	const live = items.filter(b => b.status !== 'CANCELLED');
	const salons = new Set<number>();
	let minutes = 0;
	let serviceValue = 0;
	for (const b of live) {
		minutes += b.durationMinutes || 0;
		serviceValue += b.amount || 0;
		if (b.location?.id != null) salons.add(b.location.id);
	}
	return { bookings: live.length, minutes, serviceValue, salons: salons.size };
};

// ─── Formatting ───────────────────────────────────────────────────────────

const wrapMinutes = (min: number): number =>
	((min % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;

/** "9:00 AM" */
export const formatClock = (min: number): string => {
	const m = wrapMinutes(min);
	const h = Math.floor(m / 60);
	const mm = m % 60;
	return `${h % 12 || 12}:${pad(mm)} ${h >= 12 ? 'PM' : 'AM'}`;
};

/** "9a", "1:30p" */
export const formatClockShort = (min: number): string => {
	const m = wrapMinutes(min);
	const h = Math.floor(m / 60);
	const mm = m % 60;
	return `${h % 12 || 12}${mm ? ':' + pad(mm) : ''}${h >= 12 ? 'p' : 'a'}`;
};

/** "4h 00m" / "45m" / "0h" */
export const formatDurationShort = (minutes: number): string => {
	if (!minutes) return '0h';
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`;
};

export const formatMoney = (amount: number): string =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export const formatDay = (key: string, options: Intl.DateTimeFormatOptions): string =>
	parseKey(key).toLocaleDateString('en-US', options);

export const formatRangeLabel = (mode: CalendarMode, range: DateRange): string => {
	if (mode === 'day')
		return formatDay(range.from, {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		});
	if (mode === 'month') return formatDay(range.from, { month: 'long', year: 'numeric' });
	return `${formatDay(range.from, { month: 'short', day: 'numeric' })} – ${formatDay(range.to, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

/** Address line for a salon location, zip included. */
export const formatAddress = (loc: {
	address?: string | null;
	city?: string | null;
	state?: string | null;
	zipCode?: string | null;
}): string =>
	[loc.address, loc.city, [loc.state, loc.zipCode].filter(Boolean).join(' ')]
		.filter(Boolean)
		.join(', ');

/** What the calendar calls a location: its name, else its city. */
export const salonShortName = (loc: {
	locationName?: string | null;
	city?: string | null;
}): string => loc.locationName || loc.city || 'Salon';

// ─── Colours ──────────────────────────────────────────────────────────────

/** One colour per location, in "My salons" order; wraps past eight. */
export const SALON_PALETTE: EventColor[] = [
	{ border: '#667eea', background: '#e0e7ff', text: '#312e81' },
	{ border: '#0891b2', background: '#cffafe', text: '#164e63' },
	{ border: '#d97706', background: '#fef3c7', text: '#78350f' },
	{ border: '#db2777', background: '#fce7f3', text: '#831843' },
	{ border: '#16a34a', background: '#dcfce7', text: '#14532d' },
	{ border: '#7c3aed', background: '#ede9fe', text: '#4c1d95' },
	{ border: '#ea580c', background: '#ffedd5', text: '#7c2d12' },
	{ border: '#0d9488', background: '#ccfbf1', text: '#134e4a' },
];

export const salonColor = (index: number): EventColor =>
	SALON_PALETTE[((index % SALON_PALETTE.length) + SALON_PALETTE.length) % SALON_PALETTE.length];

export const STATUS_COLORS: Record<CalendarStatus, EventColor & { label: string }> = {
	upcoming: { label: 'Upcoming', border: '#7c3aed', background: '#ede9fe', text: '#4c1d95' },
	previous: { label: 'Previous', border: '#2563eb', background: '#dbeafe', text: '#1e3a8a' },
	completed: { label: 'Completed', border: '#16a34a', background: '#dcfce7', text: '#14532d' },
	cancelled: { label: 'Cancelled', border: '#dc2626', background: '#fee2e2', text: '#7f1d1d' },
};

export const UNKNOWN_SALON_COLOR: EventColor = {
	border: '#9ca3af',
	background: '#f3f4f6',
	text: '#374151',
};
